import React from 'react';
import Icon from '../../../components/AppIcon';

const AnalyticsReportingPanel = ({ tickets, statistics }) => {
  const categoryDistribution = tickets?.reduce((acc, ticket) => {
    acc[ticket?.category] = (acc?.[ticket?.category] || 0) + 1;
    return acc;
  }, {});

  const priorityDistribution = tickets?.reduce((acc, ticket) => {
    acc[ticket?.priority] = (acc?.[ticket?.priority] || 0) + 1;
    return acc;
  }, {});

  const statusDistribution = tickets?.reduce((acc, ticket) => {
    acc[ticket?.ticketStatus] = (acc?.[ticket?.ticketStatus] || 0) + 1;
    return acc;
  }, {});

  const formatPercentage = (value, total) => {
    if (total === 0) return '0%';
    return `${Math.round((value / total) * 100)}%`;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">Support Analytics & Reporting</h3>
        <p className="text-sm text-muted-foreground">
          Comprehensive metrics and insights for support ticket performance
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <div className="bg-muted/50 rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Tag" size={18} className="text-primary" />
            Tickets by Category
          </h4>
          <div className="space-y-3">
            {Object.entries(categoryDistribution)?.map(([category, count]) => (
              <div key={category} className="flex items-center justify-between">
                <span className="text-sm text-foreground">
                  {category?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                </span>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-muted rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-primary h-full"
                      style={{ width: formatPercentage(count, tickets?.length) }}
                    />
                  </div>
                  <span className="text-sm font-medium text-foreground w-12 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Priority Distribution */}
        <div className="bg-muted/50 rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="AlertTriangle" size={18} className="text-primary" />
            Tickets by Priority
          </h4>
          <div className="space-y-3">
            {Object.entries(priorityDistribution)?.map(([priority, count]) => {
              const colors = {
                critical: 'bg-red-600',
                high: 'bg-orange-600',
                medium: 'bg-yellow-600',
                low: 'bg-green-600'
              };
              return (
                <div key={priority} className="flex items-center justify-between">
                  <span className="text-sm text-foreground">
                    {priority?.replace(/\b\w/g, l => l?.toUpperCase())}
                  </span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-muted rounded-full h-2 overflow-hidden">
                      <div
                        className={`${colors?.[priority]} h-full`}
                        style={{ width: formatPercentage(count, tickets?.length) }}
                      />
                    </div>
                    <span className="text-sm font-medium text-foreground w-12 text-right">{count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Status Distribution */}
        <div className="bg-muted/50 rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="BarChart3" size={18} className="text-primary" />
            Tickets by Status
          </h4>
          <div className="space-y-3">
            {Object.entries(statusDistribution)?.map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="text-sm text-foreground">
                  {status?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                </span>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-muted rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-primary h-full"
                      style={{ width: formatPercentage(count, tickets?.length) }}
                    />
                  </div>
                  <span className="text-sm font-medium text-foreground w-12 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-muted/50 rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="TrendingUp" size={18} className="text-primary" />
            Performance Metrics
          </h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Tickets</span>
              <span className="text-lg font-bold text-foreground">{statistics?.totalTickets}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Resolved Tickets</span>
              <span className="text-lg font-bold text-green-600">{statistics?.resolvedTickets}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">SLA Breached</span>
              <span className="text-lg font-bold text-red-600">{statistics?.slaBreached}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Avg Resolution Time</span>
              <span className="text-lg font-bold text-foreground">
                {Math.round(statistics?.avgResolutionTime / 60)}h {statistics?.avgResolutionTime % 60}m
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Client Satisfaction</span>
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold text-foreground">{statistics?.avgSatisfaction?.toFixed(1)}</span>
                <Icon name="Star" size={16} className="text-yellow-500" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsReportingPanel;